import { ChevronRight, Network, Rows3 } from "lucide-react";
import * as React from "react";

import type { PhaseLane as PhaseLaneModel, PhaseLaneBand } from "@/components/dag/phase-lane";
import { STATE_LABEL, STATE_STRIPE } from "@/components/runs/run-state-label";
import { cn } from "@/lib/utils";

/**
 * **The phase lane's rendering (VIS/V2).** `phase-lane.ts` decides what the
 * bands ARE and carries the argument for why; this file draws them.
 *
 * It is body content of a `VisualPanel` and paints nothing of its own beyond
 * the neutral ladder the container already sets — `--panel-well` for a band's
 * ground, `--border-subtle`, `--muted-foreground`. A band is a WELL rather than
 * a card on purpose: the well is the step the panel already cuts for a grouped
 * control, so the lane sits INSIDE the container's form instead of growing a
 * second panel vocabulary beside it (`components/viz/VisualPanel.tsx`).
 *
 * **The two places hue appears are both DATA**, which is the container's rule
 * about where accent may be spent: the lifecycle fill on a step that has a run
 * behind it, and the fan-out width. Neither is hue ALONE — the fill is followed
 * by the state said out loud for a screen reader, and the width carries its `×`
 * and its digits.
 *
 * **Every step is named.** The lane summarises the EDGES, never the nodes: all
 * fifteen steps of a full AFFDO flow appear here, in reading order, and what
 * has gone is the thirty-seven arrows between them. A summary that dropped
 * steps would be a picture of a different flow with no way for the reader to
 * tell.
 *
 * **It WRAPS rather than scrolling.** The read is left to right and the lane
 * lays out that way, but five bands forced onto one line in a 400 px pane is
 * five columns of ellipsis — the thumbnail's problem again — and a horizontal
 * scrollbar on a surface whose whole claim is "readable without zooming" is
 * worse. So bands wrap like text: still first to last, and every identifier
 * legible.
 *
 * It is an `<ol>` because the order carries the meaning; the chevrons between
 * bands are decoration and are `aria-hidden`.
 */
export interface PhaseLaneProps {
  lane: PhaseLaneModel;
  /** The lane's accessible name — the surface knows which flow this is. */
  label: string;
  /**
   * Report a step click to the caller.
   *
   * Supplied, each step becomes a button and the caller renders the detail —
   * which is how the run workspace keeps the graph's "click a step, read its
   * settings in the rail" affordance while the lane is the pane's view.
   * Omitted, the steps are plain text: a lane with no detail to open must not
   * grow buttons that do nothing, the same rule `DAGCanvasProps.onNodeSelect`
   * follows one surface up.
   */
  onSelectStep?: (name: string) => void;
  /** The caller's current selection, so the lane can mark it. */
  selectedStep?: string | null;
  className?: string;
}

export function PhaseLane({
  lane,
  label,
  onSelectStep,
  selectedStep = null,
  className,
}: PhaseLaneProps): JSX.Element {
  return (
    <ol aria-label={label} className={cn("flex flex-wrap items-stretch gap-2", className)}>
      {lane.bands.map((band, index) => (
        <li
          // Bands are positional by construction — contiguous runs of one
          // derived order — and two of them legitimately carry the same chain
          // (see the table in `phase-lane.ts`), so the index IS the identity
          // here rather than standing in for one.
          key={index}
          className="flex min-w-[12rem] flex-1 items-stretch gap-2"
        >
          <Band band={band} onSelectStep={onSelectStep} selectedStep={selectedStep} />
          {index < lane.bands.length - 1 && (
            <ChevronRight
              aria-hidden
              className="my-auto h-4 w-4 shrink-0 text-muted-foreground opacity-50"
            />
          )}
        </li>
      ))}
    </ol>
  );
}

function Band({
  band,
  onSelectStep,
  selectedStep,
}: {
  band: PhaseLaneBand;
  onSelectStep?: (name: string) => void;
  selectedStep: string | null;
}): JSX.Element {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1.5 rounded-lg border border-border-subtle bg-panel-well p-2.5">
      <p className="label-micro text-muted-foreground">
        {band.over.length === 0 ? (
          "runs once"
        ) : (
          <>
            for each{" "}
            {band.over.map((source, i) => (
              <React.Fragment key={source}>
                {i > 0 && (
                  <>
                    <span aria-hidden> × </span>
                    {/* "×" reads as nothing useful aloud, and the nesting is
                        the point: this band runs once per (fragment, torsion)
                        pair, not once per fragment. */}
                    <span className="sr-only"> and each </span>
                  </>
                )}
                <span className="font-mono normal-case">{source}</span>
              </React.Fragment>
            ))}
          </>
        )}
      </p>
      <ul className="flex flex-col gap-1">
        {band.steps.map((step) => (
          <li key={step.name} className="flex items-center gap-1.5">
            {step.status !== null && (
              <span className="flex shrink-0 items-center">
                <span
                  className={cn("h-1.5 w-1.5 rounded-full", STATE_STRIPE[step.status])}
                  aria-hidden
                />
                {/* Colour is never the only channel — `RunStateBadge` says the
                    state in words wherever there is room, and where there is
                    not (a 6px dot) it is said to a screen reader instead. */}
                <span className="sr-only">{STATE_LABEL[step.status]}: </span>
              </span>
            )}
            <StepName
              name={step.name}
              onSelect={onSelectStep}
              selected={step.name === selectedStep}
            />
            {step.width !== null && (
              <span className="shrink-0 font-mono text-[10px] font-semibold text-cyan">
                <span aria-hidden>×{step.width}</span>
                <span className="sr-only">, {step.width} runs</span>
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function StepName({
  name,
  selected,
  onSelect,
}: {
  name: string;
  selected: boolean;
  onSelect?: (name: string) => void;
}): JSX.Element {
  const className = "min-w-0 flex-1 truncate text-left font-mono text-[11px] font-medium";
  // The marker is what lets a test say "these are the flow's STEPS" — the band
  // headings above them are mono node names too, so a text query alone cannot
  // tell a step from the scope it repeats over.
  if (onSelect === undefined)
    return (
      <span data-phase-lane-step={name} className={className}>
        {name}
      </span>
    );
  return (
    <button
      type="button"
      data-phase-lane-step={name}
      onClick={() => onSelect(name)}
      aria-pressed={selected}
      className={cn(
        className,
        "rounded-sm ring-offset-background transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        selected ? "text-foreground underline decoration-dotted underline-offset-4" : null
      )}
    >
      {name}
    </button>
  );
}

/** Which reading of a flow's structure a pane is showing. */
export type FlowShapeView = "lane" | "graph";

/**
 * The switch between the two readings.
 *
 * A segmented control on the same `bg-panel-well` track as the run workspace's
 * mode switch, and with the same semantics for the same reason: a
 * `role="group"` of `aria-pressed` buttons rather than a radiogroup, because
 * each button ACTS on press — there is no committed selection separate from the
 * act — and radio semantics would mean writing roving focus for a two-item
 * group `Tab` already reaches correctly.
 *
 * It exists so the graph is never further than one press away. VIS/V2's whole
 * scope is a reading aid for the predetermined case, and a reading aid that
 * takes the graph away has replaced it instead.
 */
export function FlowShapeSwitch({
  view,
  onSelect,
  className,
}: {
  view: FlowShapeView;
  onSelect: (view: FlowShapeView) => void;
  className?: string;
}): JSX.Element {
  return (
    <div
      role="group"
      aria-label="Flow structure"
      className={cn(
        "flex items-center gap-0.5 rounded-lg border border-border-subtle bg-panel-well p-[3px]",
        className
      )}
    >
      <ShapeButton
        label="Pipeline"
        icon={Rows3}
        active={view === "lane"}
        onSelect={() => onSelect("lane")}
      />
      <ShapeButton
        label="Graph"
        icon={Network}
        active={view === "graph"}
        onSelect={() => onSelect("graph")}
      />
    </div>
  );
}

function ShapeButton({
  label,
  icon: Icon,
  active,
  onSelect,
}: {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  active: boolean;
  onSelect: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium transition-colors",
        active ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
      )}
    >
      <Icon className="h-3 w-3 shrink-0" aria-hidden />
      {label}
    </button>
  );
}
